import { formatDateToView } from "./DateTime";
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import * as XLSX from 'xlsx';


// ===================================================================================================
export function sanitizeMoneyString(input) {
    // Remove all characters except digits and a single optional decimal point
    let sanitized = input.replace(/[^0-9.]/g, '');

    // Ensure only one decimal point exists
    sanitized = sanitized.split('.').slice(0, 2).join('.');

    // Parse as a float and format to two decimal places
    const amount = parseFloat(sanitized) || 0;

    // Return the amount formatted as a string with 2 decimal places
    return amount.toFixed(2);
}

// ===================================================================================================
export const capitalize = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
};

// ===================================================================================================
export const getTableHeaderStyles = (theme) => {
    return (
        {
            padding: '8px',
            border: '1px solid',
            borderColor: theme === 'dark' ? '#555' : '#ddd'
        }
    )
};

// ===================================================================================================
export const getStatusCellStyles = (theme, status) => {
    // Return the style object
    return {
        color: status ? (theme === 'dark' ? '#4CAF50' : '#388E3C') : (theme === 'dark' ? '#FF5252' : '#F44336'),
        fontWeight: 'bold',
        fontSize: '20px', // Increase size for better visibility
        display: 'flex',
        justifyContent: 'center', // Center horizontally
        alignItems: 'center', // Center vertically
    };
};

// ===================================================================================================
export function generateFilenameWithRandomString(baseFilename, extension) {
    // Generate a random string with 8 alphanumeric characters
    const randomString = Math.random().toString(36).substring(2, 10);
    
    // Append the random string to the base filename
    const filename = `${baseFilename}_${randomString}.${extension}`;
    
    return filename;
}

//-------------------------------------------------------------------------------------------------------------------

export const removeUnderscore = (string) => {
    return string.replace(/_/g, ' ');
};

//-------------------------------------------------------------------------------------------------------------------

export function sortList(strings, key) {
    // Use the sort method to sort objects by the specified key alphabetically
    return strings.sort((a, b) => a[key].localeCompare(b[key]));
}

//-------------------------------------------------------------------------------------------------------------------

export function sortByNumber(arr, key) {
    return arr.sort((a, b) => (a[key] || 0) - (b[key] || 0));
}


//-------------------------------------------------------------------------------------------------------------------
const renderCheck = (checkValue) => {
    if (checkValue === null) {
        return null; // Show nothing for null values
    }
    return checkValue ? (
        '✔️'
    ) : (
        '❌'
    );
};

export const formatCheckDataForDownload = (checks) => {
    // Get all unique defect types from all checks
    const allDefectTypesMap = new Map();
    
    checks.forEach(check => {
        if (check.available_defect_types) {
            check.available_defect_types.forEach(defectType => {
                if (!allDefectTypesMap.has(defectType.id)) {
                    allDefectTypesMap.set(defectType.id, {
                        id: defectType.id,
                        defect: defectType.defect,
                        position: defectType.position || 999
                    });
                }
            });
        }
    });
    
    // Sort defect types by position
    const allDefectTypes = Array.from(allDefectTypesMap.values()).sort((a, b) => a.position - b.position);

    return checks.map(check => {
        // Flatten defect information into a string using the new dynamic structure
        const defectSummary = check.defects && check.defects.length > 0
            ? check.defects.map(defect => {
                    const defectTypeName = defect.defect_type_name || defect.defect_types?.defect || 'Unknown Defect';
                    if (defect.repairer_name) {
                        return `${defectTypeName} (Repaired by ${defect.repairer_name}: ${defect.repairer_comment || 'No comment'})`;
                    }
                    return `${defectTypeName} (${defect.status}: ${defect.comments || 'No comment'})`;
              }).join('; ') // Separate each defect with a semicolon
            : 'No defects found';

        // Create base check data
        const baseData = {
            Registration: check.reg,
            Driver: check.name,
            Date: check.date ? formatDateToView(check.date) : 'N/A',
            Duration: check.duration || 'N/A',
            Status: check.check_status || 'N/A',
            Mileage: check.mileage || 'N/A',
        };

        // Add dynamic defect type columns
        allDefectTypes.forEach(defectType => {
            const defectName = defectType.defect;
            
            // Check if this defect type is available for this vehicle type
            const isAvailable = check.available_defect_types && 
                check.available_defect_types.some(dt => dt.id === defectType.id);
            
            if (!isAvailable) {
                // Leave blank if this defect type doesn't apply to this vehicle type
                baseData[`${defectName} OK`] = '';
            } else {
                // Check if this defect type failed (is in failed_defect_type_ids)
                const hasFailed = check.failed_defect_type_ids && 
                    check.failed_defect_type_ids.includes(defectType.id);
                
                // For display, we show ❌ if it failed, ✔️ if it passed
                baseData[`${defectName} OK`] = hasFailed ? '❌' : '✔️';
            }
        });

        // Calculate and add "All Passed" status
        const getAllPassedStatus = () => {
            if (!check.available_defect_types || check.available_defect_types.length === 0) {
                return check.all_passed ? '✔️' : '❌';
            }
            
            // Check if any of the available defect types failed
            const hasAnyFailed = check.available_defect_types.some(defectType => 
                check.failed_defect_type_ids && check.failed_defect_type_ids.includes(defectType.id)
            );
            
            return hasAnyFailed ? '❌' : '✔️';
        };

        baseData['All Passed'] = getAllPassedStatus();
        baseData['Defects'] = defectSummary;

        // Add additional check details if available
        if (check.external_trailer) {
            baseData['External Trailer'] = check.external_trailer;
        }
        if (check.trailer_dropped !== undefined) {
            baseData['Trailer Dropped'] = check.trailer_dropped ? '✔️' : '❌';
        }
        if (check.comments && check.comments.length > 0) {
            baseData['Comments'] = check.comments.join('; ');
        }

        return baseData;
    });
};

//-------------------------------------------------------------------------------------------------------------------
// Format date range to fetch routes from database
const formatDateString = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0'); // months are 0-indexed
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}/${mm}/${dd}`;
};
    
export const getDateRangeForQuery = (input) => {
    const year = input.getFullYear();
    const month = input.getMonth(); // 0-indexed

    // Current month
    const startOfMonth = new Date(year, month, 1);
    const endOfMonth = new Date(year, month + 1, 0);

    // 7 days from end of previous month
    const endOfPrevMonth = new Date(year, month, 0); // last day of previous month
    const startPrevRange = new Date(endOfPrevMonth);
    startPrevRange.setDate(endOfPrevMonth.getDate() - 6);

    // 7 days into next month
    const startNextRange = new Date(year, month + 1, 1);
    const endNextRange = new Date(year, month + 1, 6);

    return {
    from: formatDateString(startPrevRange),
    to: formatDateString(endNextRange),
    };
};

//-------------------------------------------------------------------------------------------------------------------
export const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-');
    return `${day}-${month}-${year}`;
};

//-------------------------------------------------------------------------------------------------------------------

export const downloadReportCSV = (records) => {
    if (!records.length) return;

    const sorted = [...records].sort((a, b) => {
        const dateCompare = dayjs(a.date).valueOf() - dayjs(b.date).valueOf();
        if (dateCompare !== 0) return dateCompare;
        return a.job.localeCompare(b.job);
    });

    const headers = [
        'Date', 'Job', 'Account Ref', 'Address', 'Postcode', 'Route', 'Job Comments', 'Driver', 'Costings', 'Job Id', 'SubContracted', 'Sub Price', 'Sub Ref'
    ];

    const rows = sorted.map(record => [
        dayjs(record.date).format('DD/MM/YYYY'),
        record.job,
        record.account_ref || '',
        record.address || '',
        record.postcode || '',
        record.route || '',
        record.job_comments || '',
        record.driver || '',
        record.costings || '',
        record.id || '',
        record.subbed_company || '',
        record.subbed_price || '',
        record.subbed_ref || '',
    ]);

    const csvContent =
        [headers, ...rows]
        .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        .join('\n');

    const firstDate = dayjs(sorted[0].date);
    const fileName = `${uuidv4()}-Reports.csv`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
};

//-------------------------------------------------------------------------------------------------------------------

export const downloadSnapXLSX = (records) => {
    if (!records.length) return;

    const sorted = [...records]
        .filter(record => record.pallet_count > 0)
        .sort((a, b) => {
            const parse = (lane) => {
                const match = lane.match(/^([A-Z]+)(\d+)$/i);
                return match ? [match[1], parseInt(match[2], 10)] : [lane, 0];
            };
            const [prefixA, numA] = parse(a.lane_id || '');
            const [prefixB, numB] = parse(b.lane_id || '');

            return prefixA.localeCompare(prefixB) || numA - numB;
        });

    const worksheetData = [
        ['Lane Id', 'Product ID', 'Description', 'Items Per Pallet', 'Pallet Count', 'Total Items'],
        ...sorted.map(record => [
            record.lane_id || '',
            record.product_id || '',
            record.product_description || '',
            record.items_per_pallet || '',
            record.pallet_count || '',
            record.total_items || '',
        ]),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');

    const fileName = `${uuidv4()}-Report.xlsx`;
    XLSX.writeFile(workbook, fileName);
};

//-------------------------------------------------------------------------------------------------------------------

export const downloadCSV = (data) => {
    if (!data || !data.length) return;

    // Dynamically extract headers
    const headers = Object.keys(data[0]);

    // Build CSV rows
    const rows = data.map(row =>
        headers.map(header =>
        `"${String(row[header] ?? '').replace(/"/g, '""')}"`
        )
    );

    // Combine header + data
    const csvContent = [
        headers.map(h => `"${h}"`).join(','),
        ...rows.map(r => r.join(','))
    ].join('\n');

    // Create file and download
    const fileName = `${uuidv4()}.csv`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
};

